/* VISUAL CONSISTENCY: ONE image-size system (DESIGN_SYSTEM.md §13 — Images).
   Every upload context has exactly one aspect ratio for LAYOUT: renders sit inside fixed
   aspect-ratio containers with object-fit:cover, so a wrong-ratio image can never change a
   card or section's height. STORAGE is a separate concern: masjid PHOTO contexts
   (masjidGallery/masjidCard) store c_limit renditions — full image, no discarded pixels —
   because a crop baked into the stored URL can never be undone by any viewer. Design-shape
   contexts (avatar/logo/OG/campaign/announcement banners) intentionally keep c_fill: a square
   avatar or a 1200x630 OG image IS the deliverable, not a photo of record. */

export type ImageContext =
  | 'masjidGallery'   // 4:3
  | 'masjidCard'      // 16:10
  | 'campaign'        // 16:9 (card + banner)
  | 'announcement'    // 16:9
  | 'businessLogo'    // 1:1
  | 'avatar'          // 1:1 (profile/committee avatars — watermarked)
  | 'janazaPortrait'  // 1:1 EXEMPT — deceased portrait, dignity over branding (NO mark)
  | 'og'              // 1200x630
  | 'document'        // EXEMPT — a document/scan uploaded as an image (legibility first)
  | 'evidence'        // EXEMPT — waqf/shaheed evidence or source scan (never watermarked)

export const IMAGE_CONTEXTS: Record<ImageContext, {
  ratio: string            // CSS aspect-ratio value
  w: number                // normalized max width
  h: number                // normalized max height
  transformation: string   // Cloudinary incoming/display transformation
  preset: string           // Cloudinary upload preset name (configure in dashboard)
}> = {
  /* MASJID PHOTOS: c_limit, NOT c_fill (production incident fix). c_fill DISCARDED pixels to
     force the ratio and the crop was baked into the STORED URL — no viewer could ever show the
     full photo. c_limit bounds the longest side (so we never serve 12MP originals) and
     preserves the aspect; thumbnails may still cover-crop for LAYOUT via the `ratio` CSS
     containers (presentation, reversible), but the stored/full-size rendition is whole. */
  masjidGallery: { ratio: '4 / 3',   w: 1600, h: 1200, transformation: 'c_limit,w_1600,h_1600,f_auto,q_auto', preset: 'masjids_gallery' },
  masjidCard:    { ratio: '16 / 10', w: 1280, h: 800,  transformation: 'c_limit,w_1280,h_1280,f_auto,q_auto', preset: 'masjids_card' },
  campaign:      { ratio: '16 / 9',  w: 1280, h: 720,  transformation: 'c_fill,g_auto,w_1280,h_720,f_auto,q_auto',  preset: 'masjids_campaign' },
  announcement:  { ratio: '16 / 9',  w: 1280, h: 720,  transformation: 'c_fill,g_auto,w_1280,h_720,f_auto,q_auto',  preset: 'masjids_announcement' },
  businessLogo:  { ratio: '1 / 1',   w: 512,  h: 512,  transformation: 'c_fill,g_auto,w_512,h_512,f_auto,q_auto',   preset: 'masjids_logo' },
  avatar:        { ratio: '1 / 1',   w: 400,  h: 400,  transformation: 'c_fill,g_face,w_400,h_400,f_auto,q_auto',   preset: 'masjids_avatar' },
  // Same face-crop as `avatar`, but EXEMPT from the watermark — a deceased
  // person's portrait carries no branding mark (dignity over branding).
  janazaPortrait:{ ratio: '1 / 1',   w: 400,  h: 400,  transformation: 'c_fill,g_face,w_400,h_400,f_auto,q_auto',   preset: 'masjids_avatar' },
  og:            { ratio: '1200 / 630', w: 1200, h: 630, transformation: 'c_fill,g_auto,w_1200,h_630,f_auto,q_auto', preset: 'masjids_og' },
  // EXEMPT contexts: NO crop (preserve full legible content), only format/quality.
  // Never carry the watermark — documents and evidence must stay fully readable.
  document:      { ratio: '4 / 3',   w: 2400, h: 1800, transformation: 'c_limit,w_2400,h_2400,f_auto,q_auto', preset: 'masjids_uploads' },
  evidence:      { ratio: '4 / 3',   w: 2400, h: 1800, transformation: 'c_limit,w_2400,h_2400,f_auto,q_auto', preset: 'masjids_uploads' },
}

/* ── Trust watermark (BAKED AT UPLOAD via a Cloudinary overlay) ───────────────
   A prior decision: every PUBLIC photo carries the Masjids.in mark (bottom-right,
   faint white) as a "verified via Masjids.in" signal. It's a Cloudinary overlay
   rendered into the DELIVERED bytes — not a CSS layer — so it survives download /
   WhatsApp-share (the whole point). Documents, evidence and logos are EXEMPT
   (legibility first; we never deface readable content or a brand logo).

   Single source of truth — `deliveryTransformation()` is used BOTH at upload (the
   stored URL) and by `imageUrl()` (display normalization), so the watermark can't
   drift per-surface.

   ACTIVATION (one-time, owner step): the overlay references a REAL Cloudinary
   asset by public_id, read from NEXT_PUBLIC_CLOUDINARY_WATERMARK_PUBLIC_ID. Until
   that asset is uploaded + the env var is set, `watermarkOverlay()` returns null
   and uploads are delivered UN-watermarked — so this can never break delivery by
   referencing a missing overlay asset. */
export const WATERMARKED_CONTEXTS = new Set<ImageContext>([
  'masjidGallery', 'masjidCard', 'campaign', 'announcement', 'avatar', 'og',
])

export function watermarkOverlay(): string | null {
  const id = (process.env.NEXT_PUBLIC_CLOUDINARY_WATERMARK_PUBLIC_ID || '').trim()
  if (!id) return null
  // Cloudinary layer public_ids use ':' for folder separators.
  const layer = id.replace(/^\/+/, '').replace(/\//g, ':')
  // Small (~14% of width), faint (18% opacity), bottom-right with a small inset.
  // On a face-cropped avatar (e.g. a janaza portrait) a corner mark this small
  // never covers the centered face — dignified.
  return `l_${layer},o_18,w_0.14,fl_relative,g_south_east,x_20,y_20`
}

/** The full Cloudinary transformation for a context — the design-system ratio,
 *  plus the trust watermark for public-photo contexts (when configured). */
export function deliveryTransformation(context: ImageContext): string {
  const base = IMAGE_CONTEXTS[context].transformation
  if (!WATERMARKED_CONTEXTS.has(context)) return base
  const wm = watermarkOverlay()
  return wm ? `${base}/${wm}` : base
}

/* Rewrite a Cloudinary delivery URL to the context's normalized rendition
   (watermark included for public-photo contexts). Non-Cloudinary URLs (brand
   placeholders, local assets) pass through. */
export function imageUrl(url: string | null | undefined, context: ImageContext): string | null {
  if (!url) return null
  const m = url.match(/^(https?:\/\/res\.cloudinary\.com\/[^/]+\/image\/upload\/)(?:[^/]*\/)??(v\d+\/.+|[^/]+)$/)
  if (!m) return url
  return `${m[1]}${deliveryTransformation(context)}/${m[2]}`
}
